/**
 * Builds the PATCH body for the App Settings page.
 *
 * Extracted from the page so the dot-path rule can be tested directly. It is
 * worth its own module because getting it wrong is silent: the save succeeds,
 * the page says "Settings saved", and the setting does not change.
 *
 * Storage is keyed by TOP-LEVEL setting. A field declared with a dot-path
 * (`checkInEmail.subject`) must therefore be sent as its whole root object —
 * sending the dotted key writes a literal setting named `checkInEmail.subject`
 * and leaves `checkInEmail` null. Both of that setting's fields sat in
 * production in exactly that state, having never applied. The backend now
 * rejects a dotted key outright (`_normalise` in `app_settings_service.py`), so
 * the two ends cannot disagree about this again.
 */

import type { SettingsGroup } from './settingsSchema';

export function getValue(obj: Record<string, unknown>, key: string): unknown {
  if (!key.includes('.')) return obj[key];
  let cur: unknown = obj;
  for (const part of key.split('.')) {
    if (cur && typeof cur === 'object') {
      cur = (cur as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }
  return cur;
}

export function setValue(
  obj: Record<string, unknown>,
  key: string,
  value: unknown,
): Record<string, unknown> {
  if (!key.includes('.')) return { ...obj, [key]: value };
  const parts = key.split('.');
  const next = { ...obj };
  let cur: Record<string, unknown> = next;
  for (let i = 0; i < parts.length - 1; i++) {
    const p = parts[i];
    const child = cur[p];
    const childObj =
      child && typeof child === 'object' ? { ...(child as Record<string, unknown>) } : {};
    cur[p] = childObj;
    cur = childObj;
  }
  cur[parts[parts.length - 1]] = value;
  return next;
}

/**
 * Only the settings that actually changed, keyed by top-level name.
 *
 * The root object comes from the draft rather than being assembled from the
 * changed field, so editing one field of a nested setting does not blank its
 * siblings.
 */
export function buildSettingsPatch(
  draft: Record<string, unknown>,
  saved: Record<string, unknown> | null,
  schema: readonly SettingsGroup[],
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const group of schema) {
    for (const field of group.fields) {
      const before = saved ? getValue(saved, field.key) : undefined;
      const after = getValue(draft, field.key);
      if (JSON.stringify(before) !== JSON.stringify(after)) {
        const root = field.key.split('.')[0];
        patch[root] = getValue(draft, root);
      }
    }
  }
  return patch;
}
